import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  GetCommentsAttachedFileQueryResult,
  GetCommentsAttachedFilesQueryResult,
  GetCommentsAttachedThreadFilesQueryResult,
  PostCommentsDownloadMutationResult,
  useGetCommentsAttachedFile,
  useGetCommentsAttachedFiles,
  useGetCommentsAttachedThreadFiles,
  usePostCommentsDownload,
  usePostCommentsSearch,
} from '../../../../api/comments/comments';
import {
  AttachedFile,
  CommentSearchConditions,
  CommentThread,
  CommentTree,
  CommentTreeChapter,
  CommentTreeSection,
  ProductionMasters,
} from '../../../../api/model';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import { executeDownload } from '../../../../utils/download';
import { CheckBox } from '../../../atoms/CheckBox';
import ErrorComponent from '../../../atoms/ErrorComponent';
import Link from '../../../atoms/Link';
import MultiSelect from '../../../atoms/MultiSelect';
import SingleSelect from '../../../atoms/SingleSelect';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ErrorModal from '../../../modules/ErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface SearchedCommentItem {
  commentId: string;
  chapterId: number;
  chapter: string;
  chapterItemsCount: number | undefined;
  section: string;
  sectionIndex: number;
  sectionItemsCount: number | undefined;
  updated: string;
  threadDivision: string;
  threadIndex: number;
  threadItemsCount: number | undefined;
  itemContent: string;
  itemIndex: number;
  attachedFiles: AttachedFile[] | undefined;
  completed: boolean;
  sectionId: number;
  hideHistory: boolean;
  commentThreadId: string;
}

interface Props {
  productionId: number;
  masters: ProductionMasters;
}

/**
 * 懸案項目タブ
 * @param param0
 * @returns
 */
function ProductionCommentsTab({ productionId, masters }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const schema = yup.object({
    title: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
  });

  const { register, handleSubmit, control } = useForm({
    defaultValues: {} as CommentSearchConditions,
    resolver: yupResolver(schema),
  });

  // 検索処理定義
  const options = useDefaultOptions(language);

  const {
    data: commentSearchResponse,
    mutateAsync: commentsSearchAsync,
    isError: isCommentsSearchError,
    error: commentsSearchError,
    isLoading: isCommentsSearchLoading,
  } = usePostCommentsSearch(options);

  const [displayCommentsSearchError, setDisplayCommentsSearchError] = useState(false);

  useEffect(() => {
    setDisplayCommentsSearchError(commentsSearchError != null);
  }, [commentsSearchError]);

  // 検索結果のソート
  const [sortInfo, setSortInfo] = useState({
    sortkey: {} as keyof SearchedCommentItem,
    isDesc: false,
  });

  const sortedCommentTreeChapters = useMemo(() => {
    const compareStringValues = (valueA: string, valueB: string, direction: number): number => {
      if (valueA < valueB) {
        return -1 * direction;
      }
      if (valueA > valueB) {
        return 1 * direction;
      }
      return 0;
    };

    const sortComments = (chapters: CommentTreeChapter[]): CommentTreeChapter[] => {
      const direction = sortInfo.isDesc ? -1 : 1;

      if (sortInfo.sortkey === 'chapter') {
        return chapters.sort((chapterA, chapterB) =>
          compareStringValues(chapterA.title ?? '', chapterB.title ?? '', direction)
        );
      }

      if (sortInfo.sortkey === 'section') {
        chapters.forEach((chapter) => {
          // eslint-disable-next-line no-param-reassign
          chapter.sections = chapter.sections?.sort((sectionA, sectionB) =>
            compareStringValues(sectionA.title ?? '', sectionB.title ?? '', direction)
          );
        });
        return chapters;
      }

      if (sortInfo.sortkey === 'threadDivision') {
        chapters.forEach((chapter) => {
          chapter.sections?.forEach((section) => {
            // eslint-disable-next-line no-param-reassign
            section.commentThreads = section.commentThreads?.sort((threadA, threadB) =>
              compareStringValues(threadA.comment_division_name ?? '', threadB.comment_division_name ?? '', direction)
            );
          });
        });
        return chapters;
      }

      if (sortInfo.sortkey === 'completed') {
        chapters.forEach((chapter) => {
          chapter.sections?.forEach((section) => {
            // eslint-disable-next-line no-param-reassign
            section.commentThreads = section.commentThreads?.sort((threadA, threadB) =>
              compareStringValues(threadA.completed?.toString() ?? '', threadB.completed?.toString() ?? '', direction)
            );
          });
        });
        return chapters;
      }

      if (sortInfo.sortkey === 'itemContent') {
        chapters.forEach((chapter) => {
          chapter.sections?.forEach((section) => {
            section.commentThreads?.forEach((thread) => {
              // eslint-disable-next-line no-param-reassign
              thread.commentListItems = thread.commentListItems?.sort((itemA, itemB) =>
                compareStringValues(itemA.content ?? '', itemB.content ?? '', direction)
              );
            });
          });
        });
        return chapters;
      }

      if (sortInfo.sortkey === 'updated') {
        chapters.forEach((chapter) => {
          chapter.sections?.forEach((section) => {
            section.commentThreads?.forEach((thread) => {
              // eslint-disable-next-line no-param-reassign
              thread.commentListItems = thread.commentListItems?.sort((itemA, itemB) =>
                compareStringValues(itemA.created_at ?? '', itemB.created_at ?? '', direction)
              );
            });
            // eslint-disable-next-line no-param-reassign
            section.commentThreads = section.commentThreads?.sort((itemA, itemB) =>
              compareStringValues(
                itemA.commentListItems?.[0].created_at ?? '',
                itemB.commentListItems?.[0].created_at ?? '',
                direction
              )
            );
          });
          // eslint-disable-next-line no-param-reassign
          chapter.sections = chapter.sections?.sort((itemA, itemB) =>
            compareStringValues(
              itemA.commentThreads?.[0].commentListItems?.[0].created_at ?? '',
              itemB.commentThreads?.[0].commentListItems?.[0].created_at ?? '',
              direction
            )
          );
        });
        return chapters;
      }

      return chapters;
    };

    return [...sortComments(commentSearchResponse?.data.chapters ?? [])];
  }, [commentSearchResponse?.data.chapters, sortInfo.isDesc, sortInfo.sortkey]);

  // ソートされた検索結果から表示用データー生成
  const searchedCommentItems = useMemo(() => {
    const countChapterItems = (sections: CommentTreeSection[] | undefined): number => {
      let count = 0;
      sections?.forEach((section) =>
        section.commentThreads?.forEach((thread) => {
          count += thread.commentListItems?.length ?? 0;
        })
      );
      return count;
    };

    const countSectionItems = (threads: CommentThread[] | undefined): number =>
      threads?.reduce((pre, current) => pre + (current.commentListItems?.length ?? 0), 0) ?? 0;

    return sortedCommentTreeChapters?.flatMap(
      (chapter, chapterIndex) =>
        chapter.sections?.flatMap(
          (section, sectionIndex) =>
            section.commentThreads?.flatMap(
              (thread, threadIndex) =>
                thread.commentListItems?.flatMap(
                  (item, itemIndex) =>
                    ({
                      commentId: item.comment_id,
                      chapter: chapter.title,
                      chapterId: chapter.chapter_id,
                      chapterIndex,
                      chapterItemsCount:
                        itemIndex === 0 && threadIndex === 0 && sectionIndex === 0
                          ? countChapterItems(chapter.sections)
                          : undefined,
                      section: section.title,
                      sectionIndex,
                      sectionItemsCount:
                        itemIndex === 0 && threadIndex === 0 ? countSectionItems(section.commentThreads) : undefined,
                      updated: item.created_at,
                      threadDivision: thread.comment_division_name,
                      threadIndex,
                      threadItemsCount: itemIndex === 0 ? thread.commentListItems?.length : undefined,
                      itemContent: item.content,
                      itemIndex,
                      attachedFiles: item.attached_files,
                      completed: thread.completed,
                      sectionId: section.section_id,
                      hideHistory: thread.hide_history,
                      commentThreadId: thread.comment_thread_id,
                    } as SearchedCommentItem)
                ) ?? []
            ) ?? []
        ) ?? []
    );
  }, [sortedCommentTreeChapters]);

  // ソートクリック時の処理
  const onSortClicked = useCallback((key: keyof SearchedCommentItem) => {
    setSortInfo((preSortInfo) => ({ sortkey: key, isDesc: preSortInfo.sortkey === key && !preSortInfo.isDesc }));
  }, []);

  // 検索処理
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault();

      await handleSubmit(async (data) => {
        const conditions = { ...data };
        if (Array.isArray(conditions.comment_division_ids)) {
          conditions.comment_division_ids = conditions.comment_division_ids.join(',');
        }
        if (Array.isArray(conditions.chapter_number_ids)) {
          conditions.chapter_number_ids = conditions.chapter_number_ids.join(',');
        }
        setDisplaySpinnerModal(true);
        await commentsSearchAsync({ productionId, data: conditions });
        setDisplaySpinnerModal(false);
      })();
    },
    [handleSubmit, commentsSearchAsync, productionId]
  );

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className="relativefr">
          <div className="grid3 labip2 pb20">
            <div>
              <label htmlFor="chapter_number_ids">{t('章')}</label>
              {/*
              <SingleSelect
                id="chapter_number_ids"
                name="chapter_number_ids"
                allowEmpty={false}
                options={masters.chapter_numbers ?? []}
                register={register}
                getValue={(x) => x.chapter_number_id}
                getLabel={(x) => x.chapter_name}
              />
              */}

              <MultiSelect
                id="chapter_number_ids"
                name="chapter_number_ids"
                options={masters.chapter_numbers ?? []}
                control={control}
                isDisabled={false}
                getValue={(x) => x.chapter_number_id}
                getLabel={(x) => x.chapter_name}
              />
            </div>
            <div>
              <label htmlFor="my3-1">{t('L2タイトル')}</label>
              <input type="text" id="my3-1" {...register('title')} />
            </div>
            <div>
              <label htmlFor="">{t('区分')}</label>
              <MultiSelect
                id="comment_division_ids"
                name="comment_division_ids"
                options={masters.comment_divisions ?? []}
                control={control}
                getLabel={(x) => x.comment_division_name}
                getValue={(x) => x.comment_division_id}
                isDisabled={false}
              />
            </div>
            <div>
              <label htmlFor="">{t('ステータス')}</label>
              <SingleSelect
                id="completed"
                name="completed"
                allowEmpty
                options={[
                  { value: 1, label: t('完了') },
                  { value: 0, label: t('未完了') },
                ]}
                register={register}
                getValue={(x) => x.value}
                getLabel={(x) => x.label}
              />
            </div>
          </div>
          <div className="controlfr w50">
            <span className="ck">
              <input type="checkbox" id="ck22-rireki" {...register('hide_history')} />
              <label htmlFor="ck22-rireki">{t('履歴非表示')}</label>
            </span>
            <span>
              <input type="submit" id="" name="" value={t('検索').toString()} className="searchbtn" />
            </span>
          </div>
        </div>
      </form>
      {(() => {
        if (isCommentsSearchError) {
          return <ErrorComponent error={commentsSearchError} />;
        }
        if (isCommentsSearchLoading || commentSearchResponse == null) {
          return (
            <ProductionCommentList
              productionId={productionId}
              commentItems={searchedCommentItems}
              sortedCommentTreeChapters={undefined}
              onSortClicked={onSortClicked}
            />
          );
        }
        return (
          <ProductionCommentList
            productionId={productionId}
            commentItems={searchedCommentItems}
            sortedCommentTreeChapters={sortedCommentTreeChapters}
            onSortClicked={onSortClicked}
          />
        );
      })()}
      {displaySpinnerModal && <SpinnerModal />}
      {displayCommentsSearchError && (
        <ErrorModal
          error={commentsSearchError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayCommentsSearchError(false);
          }}
        />
      )}
    </>
  );
}

export default ProductionCommentsTab;

/**
 * 懸案項目の検索結果一覧
 */
interface ProductionCommentListProps {
  productionId: number;
  commentItems: SearchedCommentItem[];
  sortedCommentTreeChapters: CommentTreeChapter[] | undefined;
  onSortClicked: (key: keyof SearchedCommentItem) => void | undefined;
}

function ProductionCommentList({
  productionId,
  commentItems,
  sortedCommentTreeChapters,
  onSortClicked,
}: ProductionCommentListProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const { register, handleSubmit, setValue } = useForm<{ chapters: boolean[] }>();

  // 出力処理定義
  const options = useOptionsForBinary(language);

  const { mutateAsync: commentsDownloadAsync, error: commentsDownloadError } = usePostCommentsDownload<
    AxiosError<Blob>,
    PostCommentsDownloadMutationResult
  >(options);

  const [displayCommentsDownloadError, setDisplayCommentsDownloadError] = useState(false);

  useEffect(() => setDisplayCommentsDownloadError(commentsDownloadError != null), [commentsDownloadError]);

  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

  // 出力のダウンロード処理
  const downloadComments = useCallback(
    async (checkedChapters: CommentTreeChapter[] | undefined) => {
      if (checkedChapters == null || checkedChapters.length < 1) {
        return;
      }

      setDisplaySpinnerModal(true);

      const response = await commentsDownloadAsync({
        productionId,
        data: { chapters: checkedChapters } as CommentTree,
      });

      setDisplaySpinnerModal(false);

      if (response.data != null) {
        executeDownload(response);
      }
    },
    [commentsDownloadAsync, productionId]
  );

  // 出力処理
  const onOutputClicked = useCallback(async () => {
    await handleSubmit(async (data) => {
      const checkedChapters = sortedCommentTreeChapters?.filter(
        (chapter) => data.chapters[chapter.chapter_id ?? 0] === true
      );
      await downloadComments(checkedChapters);
    })();
  }, [handleSubmit, sortedCommentTreeChapters, downloadComments]);

  // 行毎の出力処理
  const onRowOutputClicked = useCallback(
    async (chapterId: number) => {
      const checkedChapters = sortedCommentTreeChapters?.filter((chapter) => (chapter.chapter_id ?? 0) === chapterId);
      await downloadComments(checkedChapters);
    },
    [sortedCommentTreeChapters, downloadComments]
  );

  // 行毎のメニュー開閉処理
  const [chapterIdForOpenedMenu, setChapterIdForOpenedMenu] = useState<number>();

  const onOpenMenuClicked = useCallback(
    (chapterId: number) =>
      chapterId === chapterIdForOpenedMenu ? setChapterIdForOpenedMenu(0) : setChapterIdForOpenedMenu(chapterId),
    [chapterIdForOpenedMenu]
  );

  // 全チェック処理
  const [allChecked, setAllChecked] = useState(false);

  useEffect(() => {
    commentItems.forEach((item) => setValue(`chapters.${item.chapterId}`, allChecked));
  }, [commentItems, setValue, allChecked]);

  return (
    <>
      <form>
        <div className="outputfr mt20 pl15">
          <button type="button" className="text-button" onClick={() => onOutputClicked()}>
            <span>
              <i className="fa-solid fa-download" />
              {t('出力')}
            </span>
          </button>
        </div>
        <div className="tableWrapper15 mt20">
          <table className="table15">
            <thead>
              <tr>
                <th>
                  <span className="ck-box">
                    <input
                      type="checkbox"
                      id="ck22-1"
                      checked={allChecked}
                      onChange={(e) => setAllChecked(e.target.checked)}
                    />{' '}
                    <label htmlFor="ck22-1">&nbsp;</label>
                  </span>
                </th>
                <th>
                  {t('章タイトル')}
                  <button type="button" className="text-button" onClick={() => onSortClicked('chapter')}>
                    <i className="fa-solid fa-sort" />
                  </button>
                </th>
                <th>
                  {t('L2タイトル')}
                  <button type="button" className="text-button" onClick={() => onSortClicked('section')}>
                    <i className="fa-solid fa-sort" />
                  </button>
                </th>
                <th>
                  {t('更新日')}
                  <button type="button" className="text-button" onClick={() => onSortClicked('updated')}>
                    <i className="fa-solid fa-sort" />
                  </button>
                </th>
                <th>
                  {t('区分')}
                  <button type="button" className="text-button" onClick={() => onSortClicked('threadDivision')}>
                    <i className="fa-solid fa-sort" />
                  </button>
                </th>
                <th>
                  {t('内容')}
                  <button type="button" className="text-button" onClick={() => onSortClicked('itemContent')}>
                    <i className="fa-solid fa-sort" />
                  </button>
                </th>
                <th>
                  {t('添付')}
                  <button type="button" className="text-button" onClick={() => onSortClicked('threadDivision')}>
                    <i className="fa-solid fa-sort" />
                  </button>
                </th>
                <th>
                  {t('ステータス')}
                  <button type="button" className="text-button" onClick={() => onSortClicked('completed')}>
                    <i className="fa-solid fa-sort" />
                  </button>
                </th>
                <th> </th>
              </tr>
            </thead>
            <tbody>
              {commentItems?.map((item) => (
                <tr key={item?.commentId}>
                  {item.chapterItemsCount != null && (
                    <td rowSpan={item.chapterItemsCount}>
                      <CheckBox name={`chapters.${item.chapterId}`} register={register} />
                    </td>
                  )}
                  {item.chapterItemsCount != null && <td rowSpan={item.chapterItemsCount}>{item.chapter}</td>}
                  {item.sectionItemsCount != null && <td rowSpan={item.sectionItemsCount}>{item.section}</td>}
                  <td style={{ width: '100px' }}>{item.updated}</td>
                  {item.threadItemsCount != null && (
                    <td style={{ width: '100px' }} rowSpan={item.threadItemsCount}>
                      {item.threadDivision}
                    </td>
                  )}
                  <td>
                    <p style={{ wordBreak: 'break-all', width: '100%' }}>
                      <Link
                        to={`/${language}/productions/sections/${productionId}/ps?section_id=${item.sectionId}`}
                        target="_blank"
                      >
                        {item.itemContent}
                      </Link>
                    </p>
                  </td>
                  <td className="clip">
                    {(item.attachedFiles?.length ?? 0) > 0 && <CommentAttachedFile item={item} />}
                  </td>
                  <td>{item.completed ? t('完了') : t('未完了')}</td>
                  {item.chapterItemsCount != null && (
                    <td className="elloutput" rowSpan={item.chapterItemsCount}>
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => onOpenMenuClicked(item.chapterId ?? '')}
                      >
                        <i className="fa-solid fa-ellipsis-vertical" />
                      </button>
                      <div className={chapterIdForOpenedMenu === item.chapterId ? 'open' : ''}>
                        <span>
                          <button
                            type="button"
                            className="text-button"
                            onClick={() => onRowOutputClicked(item.chapterId)}
                          >
                            <i className="fa-solid fa-download" />
                            {t('出力')}
                          </button>
                        </span>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </form>
      {displaySpinnerModal && <SpinnerModal />}
      {displayCommentsDownloadError && (
        <BlobErrorModal
          error={commentsDownloadError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayCommentsDownloadError(false);
          }}
        />
      )}
    </>
  );
}

interface CommentAttachedFileProps {
  item: SearchedCommentItem;
}

/**
 * 添付ファイル表示，取得用コンポーネント
 * @param param0
 * @returns
 */
function CommentAttachedFile({ item }: CommentAttachedFileProps) {
  const {
    i18n: { language },
  } = useTranslation();

  // 添付ファイル取得処理定義
  const optionsForBinary = useOptionsForBinary(language);

  const { refetch: refetchCommentsAttachedFile, error: commentsAttachedFileError } = useGetCommentsAttachedFile<
    GetCommentsAttachedFileQueryResult,
    AxiosError<Blob>
  >(item.attachedFiles?.[0]?.file_id ?? 0, optionsForBinary);

  const [displayCommentsAttachedFileError, setDisplayCommentsAttachedFileError] = useState(false);

  useEffect(() => setDisplayCommentsAttachedFileError(commentsAttachedFileError != null), [commentsAttachedFileError]);

  // 複数添付ファイル取得処理定義
  const { refetch: refetchCommentsAttachedFiles, error: commentsAttachedFilesError } = useGetCommentsAttachedFiles<
    GetCommentsAttachedFilesQueryResult,
    AxiosError<Blob>
  >(item.commentId, optionsForBinary);

  const [displayCommentsAttachedFilesError, setDisplayCommentsAttachedFilesError] = useState(false);

  useEffect(
    () => setDisplayCommentsAttachedFilesError(commentsAttachedFilesError != null),
    [commentsAttachedFilesError]
  );

  // スレッド単位添付ファイル取得処理定義
  const { refetch: refetchCommentsAttachedThreadFiles, error: commentsAttachedThreadFilesError } =
    useGetCommentsAttachedThreadFiles<GetCommentsAttachedThreadFilesQueryResult, AxiosError<Blob>>(
      item.commentThreadId,
      optionsForBinary
    );

  const [displayCommentsAttachedThreadFilesError, setDisplayCommentsAttachedThreadFilesError] = useState(false);

  useEffect(
    () => setDisplayCommentsAttachedThreadFilesError(commentsAttachedThreadFilesError != null),
    [commentsAttachedThreadFilesError]
  );

  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);

  // 添付ファイル取得処理
  const onAttachedFileClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await refetchCommentsAttachedFile();
    setDisplaySpinnerModal(false);
    if (!response.isError && response.data?.data != null) {
      executeDownload(response.data);
    }
  }, [refetchCommentsAttachedFile]);

  // 複数添付ファイル取得処理
  const onAttachedFilesClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await refetchCommentsAttachedFiles();
    setDisplaySpinnerModal(false);
    if (!response.isError && response.data?.data != null) {
      executeDownload(response.data);
    }
  }, [refetchCommentsAttachedFiles]);

  // スレッド単位の添付ファイル
  const onAttachedThreadFileClicked = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const response = await refetchCommentsAttachedThreadFiles();
    setDisplaySpinnerModal(false);
    if (!response.isError && response.data?.data != null) {
      executeDownload(response.data);
    }
  }, [refetchCommentsAttachedThreadFiles]);

  return (
    <>
      {(() => {
        if (item.hideHistory && item.attachedFiles?.length !== 0) {
          // スレッド単位の添付ファイル
          return (
            <button type="button" className="text-button" onClick={() => onAttachedThreadFileClicked()}>
              <i className="fa-solid fa-paperclip" />
            </button>
          );
        }
        if (item.attachedFiles?.length === 1) {
          return (
            <button type="button" className="text-button" onClick={() => onAttachedFileClicked()}>
              <i className="fa-solid fa-paperclip" />
            </button>
          );
        }
        if (item.attachedFiles?.length ?? 0 > 1) {
          return (
            <button type="button" className="text-button" onClick={() => onAttachedFilesClicked()}>
              <i className="fa-solid fa-paperclip" />
            </button>
          );
        }
        return null;
      })()}
      {displaySpinnerModal && <SpinnerModal />}
      {displayCommentsAttachedFileError && (
        <BlobErrorModal
          error={commentsAttachedFileError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayCommentsAttachedFileError(false);
          }}
        />
      )}
      {displayCommentsAttachedFilesError && (
        <BlobErrorModal
          error={commentsAttachedFilesError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayCommentsAttachedFilesError(false);
          }}
        />
      )}
      {displayCommentsAttachedThreadFilesError && (
        <BlobErrorModal
          error={commentsAttachedThreadFilesError}
          onClickOk={() => {
            setDisplaySpinnerModal(false);
            setDisplayCommentsAttachedThreadFilesError(false);
          }}
        />
      )}
    </>
  );
}
